import { Suspense, lazy, useEffect, useMemo } from 'react';

import LocalStorage from '@data/LocalStorage';
import AdminEnrollmentPoster from '@views/admin/AdminEnrollmentPoster';
import OAuthRedirect from '@views/admin/OAuthRedirect';
import AuthCodeView from '@views/auth/AuthCodeView';
import EmployeeLoginView from '@views/auth/EmployeeLoginView';
import SelfEnrollView from '@views/auth/SelfEnrollView';
import SetupCompleteView from '@views/auth/SetupCompleteView';
import SetupPasswordView from '@views/auth/SetupPasswordView';
import TwoFactorAuthView from '@views/auth/TwoFactorAuthView';
import OauthResponseView from '@views/auth/oauth/OauthResponseView';
import OauthSuccessView from '@views/auth/oauth/OauthSuccessView';

import SparkQuickApproveView from '@features/spark-quick-approve/views';

import AppContent from '@components/layout/AppContent';
import AppHeader from '@components/layout/AppHeader/AppHeader';
import AssumedRoleBanner from '@components/layout/AssumedRoleBanner';
import OnboardingBanner from '@components/layout/OnboardingBanner';
import PageLoading from '@components/layout/PageLoading';
import { Redirect, Route, Router, Switch, useQueryParams } from '@components/router';

import { useApp } from '@hooks/AppHooks';
import { QueryParamProvider } from '@hooks/QueryParamsHooks';

import { EmployeeLoadingFallback } from '@app/employee-app/components/EmployeeAppSkeleton';

import VersionEnforcer from './VersionEnforcer';

const EmployeeRouter = lazy(() => import('@app/employee-app/routes/EmployeeRouter'));
const AccountRouter = lazy(() => import('./AccountRouter'));
const ControlCenterRouter = lazy(() => import('./ControlCenterRouter'));
const LoginView = lazy(() => import('@views/auth/LoginView'));
const ForgotPasswordView = lazy(() => import('@views/auth/ForgotPasswordView'));

const AccountIdKey = 'sparkplug::accountId';

const PageLoadingView = () => {
    const { externalUrl } = useQueryParams();

    useEffect(() => {
        if (externalUrl != null) {
            setTimeout(() => {
                window.location.href = externalUrl;
            }, 1500);
        }
    }, []);

    return <PageLoading label="Loading page..." />;
};

const RootView = () => {
    const { appIsReady, user, isAdminApp, userLoadingStatus, logOut, setRetailerLoginRedirect } =
        useApp();

    const storedAccountId = LocalStorage.get(AccountIdKey);

    const isRetailerAdmin = useMemo(
        () => appIsReady && user?.role === 'retailer-admin' && !!user?.accounts?.length,
        [appIsReady, user?.role, user?.accounts?.length],
    );

    useEffect(() => {
        if (isRetailerAdmin) {
            setRetailerLoginRedirect(true);
        }
    }, [isRetailerAdmin, setRetailerLoginRedirect]);

    if (userLoadingStatus === 'error') {
        logOut().then(() => {
            return <Redirect to="/login" />;
        });
    }

    if (appIsReady) {
        // an admin CAN visit this route, but shouldn't be auto-redirected here on load
        if (
            !isAdminApp &&
            !['super-admin', 'brand-admin', 'retailer-admin'].includes(user?.role || '')
        ) {
            return <Redirect to="/user/sparks" />;
        }

        if (user?.accounts?.length) {
            const accountId = (storedAccountId as string) || user?.accounts[0]._id;
            if (user?.role === 'retailer-admin') {
                return <Redirect to={`/${accountId}/sparks/inbox`} />;
            }
            return <Redirect to={`/${accountId}/dashboard`} />;
        }

        return <Redirect to="/login" />;
    }

    return <></>;
};

const AppRouter = () => {
    return (
        <Router>
            <VersionEnforcer />
            <OnboardingBanner />
            <AssumedRoleBanner />
            <AppHeader />
            <AppContent>
                <QueryParamProvider ReactRouterRoute={Route}>
                    <Switch>
                        <Route
                            path="/:sparkId/sparks/approve/:quickApprovalCode"
                            component={SparkQuickApproveView}
                        />
                        <Route path="/oauth-response" component={OauthResponseView} />
                        <Route
                            path="/oauth-success/:isNewUser?/:isNewStore?"
                            component={OauthSuccessView}
                        />
                        <Route path="/login/2fa" component={TwoFactorAuthView} />
                        <Route path="/login/setup/password" component={SetupPasswordView} />
                        <Route path="/login/setup/complete" component={SetupCompleteView} />
                        <Redirect path="/login/setup" to="/login/setup/password" />
                        <Route path="/login/forgot-password">
                            <Suspense fallback={<PageLoading label="Loading..." />}>
                                <ForgotPasswordView />
                            </Suspense>
                        </Route>
                        <Route path="/login">
                            <Suspense fallback={<PageLoading label="Loading..." />}>
                                <LoginView />
                            </Suspense>
                        </Route>
                        <Route path="/employee/login" component={EmployeeLoginView} />
                        <Route path="/a" exact component={AuthCodeView} />
                        <Route path="/auth" component={AuthCodeView} />
                        <Route path="/oauth/:provider" component={OAuthRedirect} />
                        <Route path="/redirect" component={PageLoadingView} />
                        <Route path="/join" component={SelfEnrollView} />

                        <Route
                            path="/control-center"
                            component={() => (
                                <Suspense
                                    fallback={<PageLoading label="Loading Control Center..." />}
                                >
                                    <ControlCenterRouter />
                                </Suspense>
                            )}
                        />
                        <Route
                            path="/user"
                            component={() => (
                                // `EmployeeLoadingFallback` previews HomeView
                                // (header + tabs + card placeholders) on
                                // home routes and shows a plain `PageLoading`
                                // for non-home employee routes like /wallet
                                // or /profile.
                                <Suspense fallback={<EmployeeLoadingFallback />}>
                                    <EmployeeRouter />
                                </Suspense>
                            )}
                        />
                        <Route
                            path="/:accountId/settings/employees/onboarding/poster"
                            component={AdminEnrollmentPoster}
                        />
                        <Route
                            path="/:accountId"
                            component={() => (
                                <Suspense fallback={<PageLoading label="Loading..." />}>
                                    <AccountRouter />
                                </Suspense>
                            )}
                        />
                        <Route path="/" component={RootView} />
                    </Switch>
                </QueryParamProvider>
            </AppContent>
        </Router>
    );
};

export default AppRouter;
